/**
 * PURPOSE:
 * Renderizar una llave pública read-only de Karate con rondas, conectores y scroll responsive.
 *
 * RESPONSIBILITIES:
 * - Agrupar matches por ronda.
 * - Ordenar defensivamente por round, matchOrder, matchNumber e id.
 * - Mostrar cajas de combate compactas sin acciones administrativas.
 * - Mantener dimensiones internas navegables sin empujar el viewport de Hayllis.
 *
 * COLLABORATORS:
 * - KarateBracketSection.jsx
 * - PublicKarateMatchBox.jsx
 * - karateBracketUtils.js
 *
 * ROUTE:
 * src/components/Software/Karate/Categorias/components/PublicKarateBracketTree.jsx
 */

import React, { useEffect, useMemo, useState } from "react";
import PublicKarateMatchBox from "./PublicKarateMatchBox.jsx";
import { groupMatchesByRound } from "../utils/karateBracketUtils.js";

const DESKTOP_LAYOUT = {
  boxW: 236,
  boxH: 78,
  vGap: 26,
  gapX: 44,
  headerH: 30,
  finalGap: 24,
  safePad: 10,
};

const MOBILE_LAYOUT = {
  boxW: 190,
  boxH: 68,
  vGap: 22,
  gapX: 30,
  headerH: 26,
  finalGap: 18,
  safePad: 8,
};

const CONNECTOR = "#CBD5E1";
const SMALL_SCREEN_QUERY = "(max-width: 640px)";

function useIsSmallScreen() {
  const [isSmallScreen, setIsSmallScreen] = useState(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return false;
    }

    return window.matchMedia(SMALL_SCREEN_QUERY).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia(SMALL_SCREEN_QUERY);
    const handleChange = (event) => setIsSmallScreen(event.matches);

    setIsSmallScreen(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleChange);
      return () => mediaQuery.removeEventListener("change", handleChange);
    }

    mediaQuery.addListener(handleChange);
    return () => mediaQuery.removeListener(handleChange);
  }, []);

  return isSmallScreen;
}

export default function PublicKarateBracketTree({ allMatches = [] }) {
  const isSmallScreen = useIsSmallScreen();
  const layout = isSmallScreen ? MOBILE_LAYOUT : DESKTOP_LAYOUT;
  const rounds = useMemo(() => groupMatchesByRound(allMatches), [allMatches]);

  const { containerWidth, containerHeight, baseStep } = useMemo(() => {
    const baseStepValue = (layout.boxH + layout.vGap) / 2;
    const containerWidth = Math.max(1, rounds.length) * (layout.boxW + layout.gapX) + layout.finalGap + layout.safePad;
    let maxBottom = layout.headerH + layout.boxH + layout.safePad;

    rounds.forEach((round, roundIndex) => {
      const step = Math.pow(2, roundIndex) * baseStepValue;
      round.matches.forEach((_, matchIndex) => {
        const bottom = layout.headerH + (matchIndex * 2 + 1) * step + layout.boxH / 2;
        if (bottom > maxBottom) maxBottom = bottom;
      });
    });

    return {
      containerWidth,
      containerHeight: Math.ceil(maxBottom + layout.safePad),
      baseStep: baseStepValue,
    };
  }, [layout, rounds]);

  if (!rounds.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-6 text-center">
        <p className="text-sm font-black text-slate-700">La llave todavía no está disponible.</p>
      </div>
    );
  }

  return (
    <div
      className="karate-bracket-canvas relative inline-block rounded-2xl bg-slate-50 align-top"
      style={{
        width: `${containerWidth}px`,
        height: `${containerHeight}px`,
        maxWidth: "none",
      }}
    >
      {rounds.map((round, roundIndex) => {
        const colLeft = roundIndex * (layout.boxW + layout.gapX);
        const step = Math.pow(2, roundIndex) * baseStep;

        return (
          <div key={round.n} style={{ position: "absolute", left: colLeft, top: 0 }}>
            <div
              className="flex items-center justify-center rounded-full bg-white px-2 text-[10px] font-black uppercase tracking-wide text-slate-600 shadow-sm ring-1 ring-slate-200 sm:px-3 sm:text-[11px]"
              style={{ width: layout.boxW, height: layout.headerH - 4 }}
            >
              Ronda {round.n}
            </div>

            {round.matches.map((match, matchIndex) => {
              const top = layout.headerH + (matchIndex * 2 + 1) * step - layout.boxH / 2;

              return (
                <div
                  key={match?.id ?? `${round.n}-${matchIndex}`}
                  className="absolute"
                  style={{ top, width: layout.boxW, height: layout.boxH }}
                >
                  <div className="relative z-10 h-full w-full">
                    <PublicKarateMatchBox match={match} />
                  </div>

                  {roundIndex < rounds.length - 1 && (
                    <>
                      <span
                        className="absolute pointer-events-none"
                        style={{
                          top: "50%",
                          right: -layout.gapX,
                          width: layout.gapX,
                          borderTop: `1.5px solid ${CONNECTOR}`,
                          zIndex: 0,
                        }}
                      />
                      {matchIndex % 2 === 0 && (
                        <span
                          className="absolute pointer-events-none"
                          style={{
                            right: -layout.gapX,
                            top: "50%",
                            height: step * 2,
                            borderLeft: `1.5px solid ${CONNECTOR}`,
                            zIndex: 0,
                          }}
                        />
                      )}
                    </>
                  )}

                  {roundIndex === rounds.length - 1 && (
                    <span
                      className="absolute pointer-events-none"
                      style={{
                        top: "50%",
                        right: -layout.finalGap,
                        width: layout.finalGap,
                        borderTop: `1.5px solid ${CONNECTOR}`,
                        zIndex: 0,
                      }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
